import type { PrintCrs } from './printProjection'
import type { GeoPt } from './types'

/**
 * What the print server says a print centred at a point would use — the
 * answer to Tanger's `/maps/print/probe`, reached through the Estate API.
 *
 * `SUCCESS` carries everything the frame and the panel need: the CRS the sheet
 * is composed in (with its proj4 definition, so this library keeps no table of
 * projections), the ground factor, the flavours the server will print there,
 * and whether property borders can be drawn. `UNAVAILABLE` is the server's own
 * structured answer that nothing prints here — distinct from a failed lookup,
 * which is a rejected promise and leaves the previous answer standing.
 *
 * `flavors` are the print server's keywords (`basic`, `fancy`, `hybrid`,
 * `orto`), the same words `base_map` takes in a print request; see
 * `printBaseMap` for how a catalogue id becomes one.
 */
export type PrintProbe =
  | {
      status: 'SUCCESS'
      crs: PrintCrs
      flavors: readonly string[]
      /** Whether `property_borders: true` is accepted at this centre — see
       *  `printPropertyBorders`. */
      propertyBorders: boolean
      /** Optional on older deployments. Unknown capabilities are ignored. */
      capabilities?: readonly string[]
    }
  | { status: 'UNAVAILABLE' }

/** The host-supplied lookup. Rejects when it cannot ask — out of session, or a
 *  failed request — and never resolves to a made-up answer. */
export type PrintProbeLookup = (center: GeoPt) => Promise<PrintProbe>

/**
 * The CRS to compose the sheet in, or null when there is none to draw.
 *
 * `undefined` (no answer yet) and `UNAVAILABLE` both give null, and that is
 * right for the frame: neither has a rectangle worth drawing. They differ for
 * the panel, which reads `status` itself.
 */
export function printCrsOf(probe: PrintProbe | null | undefined): PrintCrs | null {
  return probe?.status === 'SUCCESS' ? probe.crs : null
}
